import Link from "next/link";
import { type Card } from "@memento/types/card";
import { type RenderConfigPhotoClip } from "@memento/types/render-config";
import { Feature, canUseFeature } from "@memento/library/feature-access";
import { Button } from "@memento/ui/Button";
import { SectionLayout } from "src/components/Renderer/SectionLayout";
import { EditClipCaption } from "src/components/Renderer/Pages/EditClip/EditClipCaption";
import { EditClipRotation } from "src/components/Renderer/Pages/EditClip/EditClipRotation";
import { EditPhotoClipDuration } from "./EditPhotoClipDuration";
import { EditClipSkipFace } from "src/components/Renderer/Pages/EditClip/EditClipSkipFace";
import { useEditor } from "src/components/Renderer/RendererProvider";
import { LockedTierBadge } from "src/components/LockedTierBadge";
import { useFeatureFlags } from "src/contexts/FeatureFlagProvider";
import { useEditorClipSettingsDoneEvent } from "src/components/Renderer/Pages/EditClip/useEditorClipSettingsDoneEvent";

type EditPhotoProps = {
  card: Card;
  clip: RenderConfigPhotoClip;
};

export const EditPhotoClipPageId = "edit-photo";

export const EditPhotoClip = ({ card, clip }: EditPhotoProps) => {
  const { featureFlags } = useFeatureFlags();
  const { getRendererPageUrl, subscription } = useEditor();
  const { onClickDone } = useEditorClipSettingsDoneEvent(clip);

  const canEditCaption = canUseFeature(
    Feature.VideoClipCustomText,
    subscription,
    {
      card,
      featureFlags,
    }
  );

  return (
    <SectionLayout variant="non-scrolling">
      <SectionLayout.Header
        onDoneUrl={getRendererPageUrl({
          cardId: card.cardId,
          cardUrl: card.cardUrl,
          pageId: "clips",
        })}
        heading="Edit Photo Clip"
        onClickDone={onClickDone}
      />
      <SectionLayout.NonScrollingContent>
        <SectionLayout.H2>
          {canEditCaption ? (
            "Caption"
          ) : (
            <div className="flex items-center gap-2">
              <LockedTierBadge feature={Feature.VideoClipCustomText} /> Caption
            </div>
          )}
        </SectionLayout.H2>
        <EditClipCaption clip={clip} card={card} />
        <SectionLayout.Separator />
        <SectionLayout.H2>Duration</SectionLayout.H2>
        <EditPhotoClipDuration clip={clip} card={card} />
        <SectionLayout.Separator />
        <SectionLayout.H2>Rotate</SectionLayout.H2>
        <EditClipRotation clip={clip} />
        <SectionLayout.Separator />
        <SectionLayout.H2>Face Collage</SectionLayout.H2>
        <EditClipSkipFace clip={clip} />
      </SectionLayout.NonScrollingContent>
    </SectionLayout>
  );
};

export const EditPhotoClipMobileFooter = ({
  card,
  clip,
}: {
  card: Card;
  clip: RenderConfigPhotoClip;
}) => {
  const { getRendererPageUrl } = useEditor();
  const { onClickDone } = useEditorClipSettingsDoneEvent(clip);

  return (
    <Link
      href={getRendererPageUrl({
        cardId: card.cardId,
        cardUrl: card.cardUrl,
        pageId: "clips",
      })}
      className="md:hidden"
    >
      <Button
        variant="primary"
        type="button"
        size="lg"
        width="full"
        onClick={onClickDone}
      >
        Done
      </Button>
    </Link>
  );
};
